import React, { useEffect, useState, useRef } from 'react';
import { Clock } from 'lucide-react';

/**
 * Timer autonome qui gère son propre état interne
 * @param {number} duration - Durée initiale en secondes
 * @param {function} onTimeUp - Callback appelé quand le temps est écoulé
 */
export const Timer = ({ duration, onTimeUp, variant = 'full' }) => {
  const [timeLeft, setTimeLeft] = useState(duration);
  const onTimeUpRef = useRef(onTimeUp);

  // Garder la référence à jour sans recréer l'effet
  useEffect(() => {
    onTimeUpRef.current = onTimeUp;
  }, [onTimeUp]);

  useEffect(() => {
    if (timeLeft === 0) {
      // Timer terminé, appeler le callback
      onTimeUpRef.current?.();
      return;
    }

    const timerId = setTimeout(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    return () => clearTimeout(timerId);
  }, [timeLeft]);

  // Déterminer la couleur selon le temps restant
  const getTimeColor = () => {
    if (timeLeft <= 60) return 'text-red-400'; // Moins d'1 minute
    if (timeLeft <= 300) return 'text-amber-400'; // Moins de 5 minutes
    return 'text-cyan-400';
  };

  const getGlowColor = () => {
    if (timeLeft <= 60) return 'shadow-red-500/30';
    if (timeLeft <= 300) return 'shadow-amber-500/30';
    return 'shadow-blue-500/30';
  };

  // Variant compact : pill (⏱ MM:SS) pour l'en-tête du quiz (mockup)
  if (variant === 'compact') {
    return (
      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-bold text-sm text-white tabular-nums" style={{ background: 'rgba(0,0,0,.2)' }}>
        <Clock className="w-4 h-4" />
        <span className={timeLeft <= 60 ? 'text-red-200' : ''}>
          {new Date(timeLeft * 1000).toISOString().substr(14, 5)}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center">
      <div className={`relative bg-slate-800/90 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl ${getGlowColor()} px-6 py-4 flex items-center gap-4`}>
        {/* Icône animée */}
        <div className="relative">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
            <Clock className="w-7 h-7 text-white" />
          </div>
          {timeLeft <= 60 && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping" />
          )}
        </div>

        {/* Texte et temps */}
        <div className="text-center">
          <p className="text-slate-400 text-xs font-medium uppercase tracking-wider">Temps restant</p>
          <p className={`text-3xl sm:text-4xl font-bold ${getTimeColor()} tabular-nums`}>
            {new Date(timeLeft * 1000).toISOString().substr(14, 5)}
          </p>
        </div>
      </div>
    </div>
  );
};